import { TWidget } from 'widgets/Widget';
import { TBundleItem as TBundleItemProduct } from 'types/models/product';

/**
 * @description Base BundleItem implementation
 * @param Widget Base widget for extending
 * @returns Bundle Item widget
 */
function BundleItemClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory product
     * @class BundleItem
     * @augments Widget
     * @classdesc Represents BundleItem component with next features:
     * 1. Handle product attributes change
     * @property {string} [data-pid] - product id
     * @property {boolean} [data-available] - availability flag
     * @property {number} [data-quantity] - quantity
     * @property {boolean} [data-is-all-attributes-selected] - Define if all product attributes are selected
     * @property {boolean} [data-disable-history] - Skip URL changes on PDP for bundle items
     * @example <caption>Order products summary</caption>
     * <div
     *     class="b-product_bundle-item"
     *     data-widget="bundleItem"
     *     data-pid="${bundledProduct.id}"
     *     data-available="${bundledProduct.available}"
     *     data-quantity="${bundledProduct.selectedQuantity}"
     *     data-disable-history="true"
     *     data-is-all-attributes-selected="${bundledProduct.isAllAttributesSelected}"
     *     data-accessibility-alerts='{
     *         "quantitychanged": "${Resource.msg('alert.quantitychanged', 'product', null)}",
     *         "variationselected": "${Resource.msg('alert.variationselected', 'product', null)}",
     *         "optionselected": "${Resource.msg('alert.optionselected', 'product', null)}"
     *     }'
     * >
     *     ... Bundle item content
     * </div>
     */
    class BundleItem extends Widget {
        changeAttributeID?: string;

        product = {} as TBundleItemProduct;

        prefs() {
            return {
                currentProductId: '',
                pid: '',
                isAllAttributesSelected: false,
                available: false,
                quantity: '0',
                disableHistory: true,
                accessibilityAlerts: {
                    optionselected: '',
                    variationselected: ''
                },
                ...super.prefs()
            };
        }

        /**
         * @description Returns general item details
         */
        getBundleInfo() {
            return {
                isAvailable: this.prefs().available,
                isAllAttributesSelected: this.prefs().isAllAttributesSelected,
                pid: this.prefs().currentProductId || this.prefs().pid,
                quantity: parseInt(this.prefs().quantity, 10)
            };
        }

        /**
         * @description Update Product View
         * @param product productModel
         */
        updateProductView(product: TBundleItemProduct): void {
            this.product = product;

            this.updatePreferences(product);
            this.renderImages(product);
            this.renderName(product);

            if (product.isAllAttributesSelected) {
                this.getById('addToCartMsg', (addToCartMsg) => {
                    addToCartMsg.hide();
                });
            }

            this.emit('toggleaddtocart');
        }

        /**
         * @description Update widget preferences
         * @param product Product object
         */
        updatePreferences(product) {
            this.setPref('currentProductId', product.id);
            this.setPref('available', product.available);
            this.setPref('isAllAttributesSelected', product.isAllAttributesSelected);
        }

        /**
         * @description Render product name
         * @param product Product object
         */
        renderName(product: TBundleItemProduct): void {
            this.render('nameTemplate', product, this.ref('nameContainer'));
        }

        /**
         * @description Render product images
         * @param product Product object
         */
        renderImages(product: TBundleItemProduct): void {
            this.render('imageTemplate', product, this.ref('imageContainer'));
        }

        /**
         * @description Placeholder for Change event
         * @param response response object
         * @param response.product - product
         */
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        afterAttributeChange(response: { product: TBundleItemProduct }): void {
            // Handled by ProductVariationChangeMixin
        }

        /**
         * @description Placeholder for alerts event
         * @param accessibilityAlert Alert message text
         */
        showAlert(accessibilityAlert: string): void {
            this.eventBus().emit('alert.show', {
                accessibilityAlert
            });

            this.changeAttributeID = undefined;
        }
    }

    return BundleItem;
}

export type TBundleItem = ReturnType<typeof BundleItemClassCreator>;

export type TBundleItemInstance = InstanceType<TBundleItem>;

export default BundleItemClassCreator;
